import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import moment from 'moment';
import { Subscription } from 'rxjs';

import { ErrorTriggers } from '../models/error-triggers.model';
import { Payment } from '../models/payment.model';
import { PaymentTerm } from '../models/payment-term.model';
import { PaymentTermSelector } from '../models/payment-term-selector.model';

import { PaymentTermsService } from '../payment-terms.service';
import { SeFeThemesLoader } from 'se-fe-themes';
import { SeFeExcludedDates } from 'se-fe-datepicker';

const ACCEPTED_DATE_FORMATS = ['MM/DD/YYYY', 'YYYY-MM-DD'];

@Component({
  selector: 'app-custom-dates-selector',
  templateUrl: './custom-dates-selector.component.html',
  styleUrls: ['./custom-dates-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CustomDatesSelectorComponent implements OnInit, OnDestroy {
  previewPercentages: number[];
  excludeDates: SeFeExcludedDates[] = [];
  paymentTermSubscription: Subscription;
  triggerErrorSubscription: Subscription;
  formBuilder: FormBuilder;
  paymentTerm: PaymentTerm;
  preSelectedCustomDates: any;
  selectedCustomDates: any;
  preSelectedTermId: string;
  hasCustomDates = false;

  @Input() paymentTermForm?: FormGroup;
  @Input() installments?: Payment[];
  @Input() saleType: string;

  constructor(private paymentTermsService: PaymentTermsService) { 
    SeFeThemesLoader.load({
      supportedThemes: 'light',
      url: 'https://se-fe-angular-services.ui.sportngin.com'
    });
  }

  ngOnInit(): void {
    const excludeEndDate = moment().add(1, 'days').format('YYYY-MM-DD');
    const excludeStartDate = moment().add(5, 'years').format('YYYY-MM-DD');
    this.excludeDates = [
      { end: excludeEndDate },
      { start: excludeStartDate }
    ];
    
    this.subscribeToPaymentTermSelection();
    this.subscribeToTriggerError();
  }

  ngOnDestroy(): void {
    this.paymentTermSubscription.unsubscribe();
    this.triggerErrorSubscription.unsubscribe();
  }

  public get customDates(): FormArray {
    return this.paymentTermForm.get('customDates') as FormArray;
  }

  public customDatesControl(): AbstractControl {
    return this.paymentTermForm.get('customDates') as FormControl;
  }

  public emitDates(sendDates: boolean): void {
    this.paymentTermsService.customDatesSubject.next({
      customDates: sendDates ? this.customDateArrayValues() : null
    });
  }

  private subscribeToPaymentTermSelection(): void {
    this.paymentTermSubscription = this.paymentTermsService.$paymentTerm
      .subscribe((paymentTermSelector: PaymentTermSelector) => {
        this.paymentTerm = paymentTermSelector.paymentTerm;
        this.formBuilder = paymentTermSelector.formBuilder;
        this.previewPercentages = this.paymentTerm ? this.paymentTerm.payment_term_strategy.preview_percentages : [];
        this.preSelectedCustomDates = paymentTermSelector.preSelectedCustomDates;
        this.preSelectedTermId = paymentTermSelector.preSelectedTermId;
        this.addOrRemoveCustomDates();
      });
  }

  private subscribeToTriggerError(): void {
    this.triggerErrorSubscription = this.paymentTermsService.$triggerError
      .subscribe((triggers: ErrorTriggers) => {
        if (this.customDates && this.customDates.invalid && triggers.triggerError) {
          this.customDates.controls.forEach((control) => {
            control.markAsTouched();
            control.setErrors({ required: true });
            control.updateValueAndValidity();
          });
        }
      });
  }

  private addOrRemoveCustomDates(): void {
    let installmentCount = 0;
    if (this.paymentTerm) {
      this.hasCustomDates = this.paymentTerm.payment_term_strategy.strategy_type === 'custom_date';
      installmentCount = Number(this.paymentTerm.payment_term_strategy.installment_count);
    }
    if (this.hasCustomDates && !this.customDates) {
      this.paymentTermForm.addControl('customDates', this.formBuilder.array([], Validators.required));
      this.addCustomDates(installmentCount);
      this.emitDates(true);
    } else if (this.hasCustomDates && this.customDates.length !== installmentCount) {
      this.customDates.clear();
      this.addCustomDates(installmentCount);
      this.emitDates(true);
    } else if (!this.hasCustomDates && this.customDates) {
      this.paymentTermForm.removeControl('customDates');
      this.emitDates(false);
    }
  }

  private customDateArrayValues(): Array<any> {
    return this.customDates.controls.map((control) => {
      if (!control.value) {
        return null;
      }

      const date = moment(control.value, ACCEPTED_DATE_FORMATS, true);
      return date.isValid() ? date.format('MM/DD/YYYY') : control.value;
    });
  }

  private addCustomDates(count: number): void {
    for (let i = 0; i < count; i++) {
      const date = (this.canSetPreSelectedCustomDates() && this.preSelectedCustomDates[i])
        ? this.preSelectedCustomDates[i] : '';
      this.customDates.push(
        new FormControl(date, { validators: [Validators.required, this.customDateValidator(i)], updateOn: 'blur' })
      );
    }
  }

  private customDateValidator(index): ValidatorFn {
    return (formControl: FormControl) => {
      const formArray = formControl.parent !== undefined ? (formControl.parent as FormArray) : null;
      if (formArray) {
        if (!formControl.value) {
          return { required: true };
        } else if (moment(formControl.value, ACCEPTED_DATE_FORMATS, true).isSameOrBefore(moment(), 'day')) {
          return { past_date_selected: true };
        } else if (moment(formControl.value, ACCEPTED_DATE_FORMATS, true).isAfter(moment().add(5, 'years'), 'day')) {
          return { invalid_future_date: true };
        } else if (this.invalidDatesOrder(index, formArray, formControl)) {
          return { dates_not_in_order: true };
        }
      }
      return null;
    };
  }

  private canSetPreSelectedCustomDates(): boolean {
    return this.hasCustomDates && Number(this.preSelectedTermId) === this.paymentTerm.id && this.preSelectedCustomDates;
  }

  private invalidDatesOrder(index: number, formArray: FormArray, formControl: FormControl): boolean {
    const previousControl = index > 0 ? formArray.at(index - 1) : null;
    if (previousControl && !previousControl.value) {
      previousControl.markAsDirty();
      previousControl.markAsTouched();
      previousControl.updateValueAndValidity();
      return false;
    }

    const currentDate = moment(formControl.value, ACCEPTED_DATE_FORMATS, true);
    if (!currentDate.isValid()) {
      return false;
    }

    const previousDate = moment(previousControl?.value, ACCEPTED_DATE_FORMATS, true);
    if (previousControl && previousDate.isValid() && previousDate.isSameOrAfter(currentDate, 'day')) {
      return true;
    }

    const nextControl = index + 1 < formArray.length ? formArray.at(index + 1) : null;
    if (!nextControl?.value) {
      return false;
    }

    nextControl.markAsDirty();
    nextControl.markAsTouched();
    nextControl.updateValueAndValidity();
    const nextDate = moment(nextControl.value, ACCEPTED_DATE_FORMATS, true);
    return nextControl.valid && nextDate.isValid() && nextDate.isSameOrBefore(currentDate, 'day');
  }
}
